import { useQuery } from '@tanstack/react-query';
import { useTranslations } from '@power-core/toolkit';
import { useState } from 'react';
import { useAppContext } from '@/modules/app-context/use-app-context';
import { useNavigation } from '@/modules/utils/custom-hooks/use-navigation';
import { getErrorMessage } from '@/modules/utils/get-error-message';
import { useProjectApi } from '../../hooks/use-project-api';
import { useAuth } from '@power-core/toolkit';

export const useProjectModalParams = () => {
	const t = useTranslations();
	const { createProject, editProject, isPending } = useProjectApi();
	const [error, setError] = useState({ message: '', details: '' });
	const { getSearchParams, addSearchParam, removeAllSearchParams } = useNavigation();
	const { customers } = useAppContext();
	const { auth } = useAuth();
	const { data: countries = [] } = useQuery({
		queryKey: ['countries'],
		queryFn: () => import('../countries.json'),
		select: (data) => data.countries,
	});
	const { title, id: documentId, country, customerId } = getSearchParams();
	const [projectState, setProjectState] = useState({
		title: title || '',
		country: country || auth?.data?.user?.country,
		customerId,
	});
	const initialState = { title, country, customerId };
	const countriesOptions = countries.map((item) => ({ value: item, label: t(item) }));
	const customerOptions = customers?.map((customer) => ({ value: customer.documentId, label: customer.name ?? '' })) ?? [];
	const customerName = customerOptions.find((customer) => customer.value === projectState.customerId)?.label;
	const countryName = countriesOptions.find((country) => country.value === projectState.country)?.label;
	const isCountrySelected = countriesOptions.find((country) => country.value === projectState.country);
	const isProjectStateUnchanged =
		projectState.title === initialState.title &&
		projectState.country === initialState.country &&
		projectState.customerId === initialState.customerId;
	const isDisabled = isPending || !!error.message || isProjectStateUnchanged;

	const onSave = () => {
		if (!projectState.country || !isCountrySelected) return setError({ message: t('T_COUNTRY_REQUIRED'), details: 'country' });
		if (!projectState.title) return setError({ message: t('T_PROJECT_NAME_REQUIRED'), details: 'title' });

		if (documentId) {
			editProject
				.mutateAsync({ body: projectState, documentId })
				.catch((err) => setError({ message: String(t(getErrorMessage(err), { title: projectState.title })), details: 'title' }));
			return;
		}

		createProject
			.mutateAsync(projectState)
			.catch((err) => setError({ message: String(t(getErrorMessage(err), { title: projectState.title })), details: 'title' }));
	};

	return {
		t,
		documentId,
		projectState,
		countriesOptions,
		customerOptions,
		customerName,
		countryName,
		isDisabled,
		isPending,
		error,
		addSearchParam,
		removeAllSearchParams,
		setError,
		onSave,
		setProjectState,
	};
};
